import React, { useState, useEffect, useRef } from 'react';
import { DirectAnimalAvatar, DirectAvatarConfig } from '../../utils/DirectAnimalAvatar';
import { PetAvatarConfig } from '../../types/user';
import {
  Dog,
  Eye,
  Ear,
  Scissors,
  Palette,
  Move,
  Check,
  Dice5,
  SlidersHorizontal,
  ChevronDown,
  Sparkles,
  PaintBucket,
  Maximize
} from 'lucide-react';

interface PetAvatarEditorProps {
  initialConfig?: Partial<PetAvatarConfig>;
  onConfigChange: (config: PetAvatarConfig, svg?: string) => void;
  baseSeed?: string;
}

// --- SUB-COMPONENTES ---

const useClickOutside = (ref: React.RefObject<HTMLElement>, handler: () => void) => {
  useEffect(() => {
    const listener = (event: MouseEvent | TouchEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

interface VisualSelectProps {
  value: number;
  max: number;
  onChange: (value: number) => void;
  partType: 'face' | 'ear' | 'eye' | 'muzzle' | 'hair' | undefined;
  color: string;
  secondaryColor?: string;
}

const VisualSelect: React.FC<VisualSelectProps> = ({ value, max, onChange, partType, color, secondaryColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useClickOutside(ref, () => setIsOpen(false));

  const getPreview = (index: number) => {
    if (!partType) return null;
    const svg = DirectAnimalAvatar.generatePartPreviewSVG(partType, index, color, secondaryColor || '#000');
    return svg;
  };

  return (
    <div className="relative font-medium" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 py-2 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
      >
        <div className="flex items-center gap-3">
          {partType && (
            <div
              className="w-8 h-8 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 p-0.5 flex-shrink-0"
              dangerouslySetInnerHTML={{ __html: getPreview(value) || '' }}
            />
          )}
          <span>Variante {value + 1}</span>
        </div>
        <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl grid grid-cols-1 gap-1 p-1">
          {Array.from({ length: max }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { onChange(i); setIsOpen(false); }}
              className={`flex items-center gap-3 w-full p-2 rounded-md transition-colors text-left text-sm ${value === i
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
            >
              {partType && (
                <div
                  className="w-10 h-10 rounded-md bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-0.5 flex-shrink-0"
                  dangerouslySetInnerHTML={{ __html: getPreview(i) || '' }}
                />
              )}
              <span>Variante {i + 1}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title }) => (
  <div className="flex items-center gap-2 mb-4 text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700 pb-2">
    <div className="text-blue-500 dark:text-blue-400">
      {icon}
    </div>
    <h3 className="text-sm font-bold uppercase tracking-wider">{title}</h3>
  </div>
);

interface SizeSliderProps {
  scale: number;
  onChange: (scale: number) => void;
  outlineWidth: number;
  onOutlineWidthChange: (width: number) => void;
}

const SizeSlider: React.FC<SizeSliderProps> = ({ scale, onChange, outlineWidth, onOutlineWidthChange }) => (
  <div className="space-y-6">
    {/* Slider de Escala */}
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <Maximize size={16} /> Tamaño del Avatar
        </label>
        <span className="text-xs font-bold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
          {Math.round(scale * 100)}%
        </span>
      </div>
      <div className="relative">
        <input
          type="range"
          min="0.5"
          max="1.5"
          step="0.05"
          value={scale}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-blue-600"
        />
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 font-medium uppercase tracking-wider">
        <span>Pequeño</span>
        <span>Normal</span>
        <span>Grande</span>
      </div>
    </div>

    {/* Slider de Grosor de Contorno Global */}
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <SlidersHorizontal size={16} /> Grosor de Bordes
        </label>
        <span className="text-xs font-bold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
          {outlineWidth}px
        </span>
      </div>
      <input
        type="range"
        min="1"
        max="6"
        step="0.5"
        value={outlineWidth}
        onChange={(e) => onOutlineWidthChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 accent-gray-500"
      />
    </div>
  </div>
);

interface ColorPickerProps {
  colors: string[];
  selectedColor: string;
  onColorSelect: (color: string) => void;
  disabled?: boolean;
  label?: string;
  compact?: boolean;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  colors,
  selectedColor,
  onColorSelect,
  disabled = false,
  label,
  compact = false
}) => {
  const quickColors = colors.slice(0, compact ? 10 : 16);
  const uniqueId = useRef(`color-${Math.random().toString(36).substr(2, 9)}`);

  return (
    <div className="space-y-2.5">
      {label && (
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase flex items-center justify-between">
          {label}
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-full border border-gray-200 dark:border-gray-600 shadow-sm"
              style={{ backgroundColor: selectedColor }}
            />
            <span className="text-[10px] text-gray-400 font-mono">{selectedColor}</span>
          </div>
        </label>
      )}

      <div className={`grid ${compact ? 'grid-cols-8' : 'grid-cols-8 sm:grid-cols-8'} gap-1.5`}>
        {/* Custom Color Input Wrapper */}
        <div className="relative group col-span-1">
          <input
            id={uniqueId.current}
            type="color"
            value={selectedColor}
            onChange={(e) => onColorSelect(e.target.value)}
            disabled={disabled}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10"
          />
          <button
            type="button"
            className={`w-full aspect-square rounded-md border-2 border-dashed flex items-center justify-center transition-all ${disabled
              ? 'border-gray-300 dark:border-gray-700 opacity-50'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 text-gray-400'
              }`}
          >
            <Palette size={compact ? 12 : 14} />
          </button>
        </div>

        {quickColors.map((color) => (
          <button
            key={color}
            type="button"
            className={`w-full aspect-square rounded-md transition-all shadow-sm ${disabled ? 'opacity-30 cursor-not-allowed' : 'hover:scale-110 hover:z-10'
              } ${selectedColor === color ? 'ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-gray-800 scale-110 z-10' : 'border border-gray-100 dark:border-gray-700'}`}
            style={{ backgroundColor: color }}
            onClick={() => !disabled && onColorSelect(color)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
};

interface AvatarPartSelectorProps {
  title: string;
  icon: React.ReactNode;
  current: number;
  max: number;
  onChange: (value: number) => void;
  color: string;
  colors: string[];
  onColorChange: (color: string) => void;
  // Per-part outline props
  outlineEnabled: boolean;
  onOutlineEnabledChange: (enabled: boolean) => void;
  outlineColor: string;
  onOutlineColorChange: (color: string) => void;
  outlineColors: string[];
  // Opcionales
  isOptional?: boolean;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  // New prop for preview
  partType?: 'face' | 'ear' | 'eye' | 'muzzle' | 'hair';
}

const AvatarPartSelector: React.FC<AvatarPartSelectorProps> = ({
  title,
  icon,
  current,
  max,
  onChange,
  color,
  colors,
  onColorChange,
  outlineEnabled,
  onOutlineEnabledChange,
  outlineColor,
  onOutlineColorChange,
  outlineColors,
  isOptional = false,
  enabled = true,
  onEnabledChange,
  partType
}) => (
  <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm transition-all duration-200 ${!enabled ? 'opacity-60 bg-gray-50 dark:bg-gray-800/50' : ''
    }`}>
    <div className="p-4 space-y-4">
      {/* Header with Title and Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${enabled ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
            {icon}
          </div>
          <span className="font-semibold text-gray-900 dark:text-gray-100">{title}</span>
        </div>

        {isOptional ? (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => onEnabledChange && onEnabledChange(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-500"></div>
          </label>
        ) : null}
      </div>

      {enabled && (
        <div className="space-y-4 animate-fadeIn">
          {/* Variant Selector - Dropdown */}
          <VisualSelect
            value={current}
            max={max}
            onChange={onChange}
            partType={partType}
            color={color}
          />

          <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

          {/* Colors Grid */}
          <div className="grid grid-cols-1 gap-4">
            <ColorPicker
              colors={colors}
              selectedColor={color}
              onColorSelect={onColorChange}
              disabled={!enabled}
              label="Color Principal"
            />
          </div>

          {/* Outline Toggle & Color */}
          <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3 border border-gray-100 dark:border-gray-700/50">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${outlineEnabled ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                Borde
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={outlineEnabled}
                  onChange={(e) => onOutlineEnabledChange(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
              </label>
            </div>

            {outlineEnabled ? (
              <ColorPicker
                colors={outlineColors}
                selectedColor={outlineColor}
                onColorSelect={onOutlineColorChange}
                compact={true}
              />
            ) : (
              <div className="text-xs text-center text-gray-400 py-1 italic">
                Sin borde activo
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  </div>
);

// --- COMPONENTE PRINCIPAL ---

const PetAvatarEditor: React.FC<PetAvatarEditorProps> = ({
  initialConfig,
  onConfigChange,
  baseSeed = 'default-pet'
}) => {
  const getInitialConfig = (): PetAvatarConfig => {
    const defaultConfig = DirectAnimalAvatar.getDefaultConfig();
    if (initialConfig && typeof initialConfig === 'object') {
      return { ...defaultConfig, ...initialConfig, useIndividualColors: true };
    }
    return { ...DirectAnimalAvatar.seedToConfig(baseSeed), useIndividualColors: true };
  };

  const [config, setConfig] = useState<PetAvatarConfig>(() => getInitialConfig());

  // Definición de variantes y colores
  const variants = DirectAnimalAvatar.getAvailableVariants();
  const primaryColors = ['#F4A460', '#DEB887', '#D2691E', '#CD853F', '#BC8F8F', '#8B4513', '#A0522D', '#D2B48C', '#F5DEB3', '#FFE4B5', '#B0C4DE', '#A9A9A9', '#C0C0C0', '#D3D3D3', '#E6E6FA', '#FFB6C1', '#FFDAB9', '#D8BFD8', '#ADD8E6', '#F0E68C'];
  const secondaryColors = ['#8B4513', '#654321', '#5D4037', '#696969', '#4A4A4A', '#FFF8DC', '#FAEBD7', '#FFEBCD', '#FFF0F5', '#F0FFF0', '#FF69B4', '#87CEEB', '#98FB98', '#DDA0DD', '#F08080'];
  const tertiaryColors = ['#FFB6C1', '#FFC0CB', '#FFE4E1', '#D2691E', '#8B4513', '#A0522D', '#FFFFFF', '#FFFAF0', '#333333', '#1C1C1C'];
  const outlineColors = ['#3d2b1f', '#000000', '#5D4037', '#2F4F4F', '#ffffff', '#8B4513', '#A52A2A', '#D2691E'];
  const backgroundColors = ['#FFF8DC', '#F0F8FF', '#F5F5F5', '#FFFAF0', '#F8F8FF', '#E6E6FA', '#FFF0F5', '#F0FFFF', '#FFFACD', '#E0FFFF'];
  const eyeColors = ['#4169E1', '#00BFFF', '#32CD32', '#228B22', '#8B4513', '#CD853F', '#FF69B4', '#8A2BE2', '#FF8C00', '#40E0D0', '#20B2AA', '#778899'];

  const gradientPresets = [
    { name: 'Atardecer', start: '#FFECD2', end: '#FCB69F' },
    { name: 'Océano', start: '#89f7fe', end: '#66a6ff' },
    { name: 'Menta', start: '#D4FC79', end: '#96E6A1' },
    { name: 'Lavanda', start: '#E0C3FC', end: '#8EC5FC' },
    { name: 'Rosa', start: '#fad0c4', end: '#ffd1ff' },
    { name: 'Melocotón', start: '#ff9a9e', end: '#fecfef' },
    { name: 'Cielo', start: '#a18cd1', end: '#fbc2eb' },
    { name: 'Bosque', start: '#84fab0', end: '#8fd3f4' },
  ];

  const updateConfig = (updates: Partial<PetAvatarConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const svg = DirectAnimalAvatar.generateSVG({ ...config, isRound: true, size: 150 });
      onConfigChange(config, svg);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [config, onConfigChange]);

  return (
    <div className="max-w-[1200px] mx-auto pb-20 p-2">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-8 mb-8 text-white shadow-xl overflow-hidden">
        <div className="absolute top-0 right-0 p-12 bg-white/10 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
        <div className="relative z-10">
          <h2 className="text-3xl font-extrabold mb-2 flex items-center gap-3">
            <Dog size={32} /> Diseño de Mascota
          </h2>
          <p className="text-blue-100 text-lg opacity-90 max-w-lg">
            Personaliza cada rasgo y contorno para crear un avatar único para tu compañero.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* COLUMNA IZQUIERDA - Controles Globales */}
        <div className="lg:col-span-4 space-y-6">

          {/* Tarjeta: Ajustes Generales */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
            <SectionHeader icon={<SlidersHorizontal size={20} />} title="Configuración Global" />
            <SizeSlider
              scale={config.avatarScale}
              onChange={(s) => updateConfig({ avatarScale: s })}
              outlineWidth={config.outlineWidth}
              onOutlineWidthChange={(w) => updateConfig({ outlineWidth: w })}
            />
          </div>

          {/* Tarjeta: Fondo */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
            <SectionHeader icon={<PaintBucket size={20} />} title="Fondo del Avatar" />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tipo de Fondo</span>
                <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => updateConfig({ useGradientBackground: false })}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${!config.useGradientBackground ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'}`}
                  >
                    Sólido
                  </button>
                  <button
                    onClick={() => updateConfig({ useGradientBackground: true })}
                    className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${config.useGradientBackground ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'}`}
                  >
                    Gradiente
                  </button>
                </div>
              </div>

              {config.useGradientBackground ? (
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {gradientPresets.map((gradient, index) => (
                    <button
                      key={index}
                      onClick={() => updateConfig({ gradientStartColor: gradient.start, gradientEndColor: gradient.end })}
                      className={`h-10 w-full rounded-lg transition-transform hover:scale-105 ${config.gradientStartColor === gradient.start ? 'ring-2 ring-blue-500 ring-offset-2' : 'border border-gray-200'
                        }`}
                      style={{ background: `linear-gradient(135deg, ${gradient.start}, ${gradient.end})` }}
                      title={gradient.name}
                    />
                  ))}
                </div>
              ) : (
                <ColorPicker
                  colors={backgroundColors}
                  selectedColor={config.backgroundColor}
                  onColorSelect={(c) => updateConfig({ backgroundColor: c })}
                />
              )}
            </div>
          </div>

          <button
            onClick={() => setConfig({ ...DirectAnimalAvatar.seedToConfig(`random_${Date.now()}`), useIndividualColors: true })}
            className="w-full py-4 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300 dark:hover:bg-indigo-900/30 rounded-xl font-bold flex items-center justify-center gap-2 transition-all border-2 border-dashed border-indigo-200 dark:border-indigo-800"
          >
            <Dice5 size={20} />
            Generar Aleatorio
          </button>
        </div>

        {/* COLUMNA DERECHA - Partes del Cuerpo */}
        <div className="lg:col-span-8 space-y-6">
          <div className="flex items-center gap-2 px-2 text-gray-400">
            <Sparkles size={18} />
            <h3 className="text-xs font-bold uppercase tracking-widest">Personalización Detallada</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AvatarPartSelector
              title="Rostro Principal" icon={<Dog />}
              current={config.faceVariant} max={variants.faces} onChange={(v) => updateConfig({ faceVariant: v })}
              color={config.faceColor} colors={primaryColors} onColorChange={(c) => updateConfig({ faceColor: c })}
              outlineEnabled={config.faceOutlineEnabled} onOutlineEnabledChange={(e) => updateConfig({ faceOutlineEnabled: e })}
              outlineColor={config.faceOutlineColor} onOutlineColorChange={(c) => updateConfig({ faceOutlineColor: c })} outlineColors={outlineColors}
              partType="face"
            />

            <AvatarPartSelector
              title="Ojos y Mirada" icon={<Eye />}
              current={config.eyeVariant} max={variants.eyes} onChange={(v) => updateConfig({ eyeVariant: v })}
              color={config.eyeColor} colors={eyeColors} onColorChange={(c) => updateConfig({ eyeColor: c })}
              outlineEnabled={config.eyeOutlineEnabled} onOutlineEnabledChange={(e) => updateConfig({ eyeOutlineEnabled: e })}
              outlineColor={config.eyeOutlineColor} onOutlineColorChange={(c) => updateConfig({ eyeOutlineColor: c })} outlineColors={outlineColors}
              partType="eye"
            />

            {/* Orejas Custom */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm col-span-1 md:col-span-2">
              <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400">
                    <Ear />
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">Orejas</span>
                </div>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="relative">
                    <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Estilo</label>
                    <VisualSelect
                      value={config.earVariant}
                      max={variants.ears}
                      onChange={(v) => updateConfig({ earVariant: v })}
                      partType="ear"
                      color={config.earColor}
                      secondaryColor={config.earInnerColor}
                    />
                  </div>

                  {/* Outline Orejas */}
                  <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-3 border border-gray-100 dark:border-gray-700/50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${config.earOutlineEnabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className="text-xs font-bold text-gray-500 uppercase">Borde</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.earOutlineEnabled}
                          onChange={(e) => updateConfig({ earOutlineEnabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-7 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
                      </label>
                    </div>

                    {config.earOutlineEnabled ? (
                      <ColorPicker
                        compact
                        colors={outlineColors}
                        selectedColor={config.earOutlineColor}
                        onColorSelect={(c) => updateConfig({ earOutlineColor: c })}
                      />
                    ) : (
                      <div className="text-xs text-center text-gray-400 py-1 italic">
                        Sin borde activo
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <ColorPicker label="Color Exterior" colors={primaryColors} selectedColor={config.earColor} onColorSelect={(c) => updateConfig({ earColor: c })} />
                  <ColorPicker label="Color Interior" colors={tertiaryColors} selectedColor={config.earInnerColor} onColorSelect={(c) => updateConfig({ earInnerColor: c })} />
                </div>
              </div>
            </div>

            <AvatarPartSelector
              title="Hocico" icon={<Move />}
              current={config.muzzleVariant} max={variants.muzzles} onChange={(v) => updateConfig({ muzzleVariant: v })}
              color={config.muzzleColor} colors={tertiaryColors} onColorChange={(c) => updateConfig({ muzzleColor: c })}
              outlineEnabled={config.muzzleOutlineEnabled} onOutlineEnabledChange={(e) => updateConfig({ muzzleOutlineEnabled: e })}
              outlineColor={config.muzzleOutlineColor} onOutlineColorChange={(c) => updateConfig({ muzzleOutlineColor: c })} outlineColors={outlineColors}
              partType="muzzle"
            />

            <AvatarPartSelector
              title="Peinado / Pelo" icon={<Scissors />}
              current={config.hairVariant} max={variants.hairs} onChange={(v) => updateConfig({ hairVariant: v })}
              color={config.hairColor} colors={primaryColors} onColorChange={(c) => updateConfig({ hairColor: c })}
              isOptional={true} enabled={config.hasHair} onEnabledChange={(e) => updateConfig({ hasHair: e })}
              outlineEnabled={config.hairOutlineEnabled} onOutlineEnabledChange={(e) => updateConfig({ hairOutlineEnabled: e })}
              outlineColor={config.hairOutlineColor} onOutlineColorChange={(c) => updateConfig({ hairOutlineColor: c })} outlineColors={outlineColors}
              partType="hair"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetAvatarEditor;